import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ProductSubscriptionReportManagementControllerService,
    ProductSubscriptionReportRequestDTO
} from '@iota/inh-core-v1-management-client';
import { ReportingControllerService } from '@iota/iota-management-portal-v4-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as saveAs from 'file-saver';
import moment from 'moment';
import { Observable, catchError, map, mergeMap, of, take } from 'rxjs';
import { ProductSubscriptionReportType } from 'src/app/administration/product-subscription-report/report-type';
import { getFilenameHeader } from 'src/app/shared/utils';
import {
    CreateFoyerCaseExport,
    GenerateProductSubscriptionReport,
    ReportActionFailure,
    ReportActionNoContent,
    ReportActionSuccess
} from '../actions/reporting.actions';

@Injectable()
export class ReportingEffects {
    generateProductSubscriptionReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GenerateProductSubscriptionReport),
            mergeMap(({ request, reportType }) =>
                this.callProductSubscriptionReport(reportType, request).pipe(
                    take(1),
                    map((res: HttpResponse<Blob>) => {
                        if (res.status === 204) {
                            return ReportActionNoContent();
                        }
                        if (res.body.size !== 0) {
                            const defaultFilename = 'ProductSubscriptionReport_' + reportType + '_' + moment().toISOString() + '.xlsx';
                            saveAs(res.body, getFilenameHeader(res, defaultFilename));
                        }

                        return ReportActionSuccess();
                    }),
                    catchError((error) => of(ReportActionFailure({ error: { originalError: error } })))
                )
            )
        )
    );

    createFoyerCaseExport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateFoyerCaseExport),
            mergeMap(({ request }) =>
                this.apiService.createFoyerCaseExport(request).pipe(
                    map((result) => {
                        if (!result?.successful) {
                            return ReportActionFailure({ error: { errorMessage: result?.errorMessage, originalError: result } });
                        }

                        return ReportActionSuccess();
                    }),
                    catchError((response) => {
                        let errorMessage = '';
                        if (response?.error?.code) {
                            errorMessage += response?.error?.code + ' - ';
                        }
                        errorMessage += response?.error?.error ?? '';

                        return of(ReportActionFailure({ error: { errorMessage, originalError: response } }));
                    })
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private apiService: ReportingControllerService,
        private productSubscriptionReportApi: ProductSubscriptionReportManagementControllerService
    ) {}

    private callProductSubscriptionReport(
        reportType: ProductSubscriptionReportType,
        request: ProductSubscriptionReportRequestDTO
    ): Observable<HttpResponse<Blob>> {
        switch (reportType) {
            case ProductSubscriptionReportType.TERMINATIONS:
                return this.productSubscriptionReportApi.generateTerminationsReport(request, 'response');
            case ProductSubscriptionReportType.NEW:
                return this.productSubscriptionReportApi.generateNewReport(request, 'response');
            case ProductSubscriptionReportType.ACTIVE:
                return this.productSubscriptionReportApi.generateActiveReport(request, 'response');
        }
    }
}
